import { type YearMonth, addMonths, monthKey, parseDayKey } from '@cadoan/shared'
import type { AttendanceStatus } from '@cadoan/shared'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Drawer } from 'vaul'
import { AppIcon, type IconName } from './icons'
import './ui.css'

export { AppIcon, ICON_REGISTRY, type IconName } from './icons'

/**
 * The shared kit, News Print (.docs/theme/design-system.md). Every control
 * is built here once, square, ink on paper, at the one control height.
 */

// ── Buttons ──────────────────────────────────────────────────────────────────

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  busy = false,
  icon,
  type = 'button',
  className = '',
}: {
  children: ReactNode
  onClick?: () => void
  /**
   * primary: ink block, the screen's one main action.
   * secondary: a 1px ink frame. danger: the same frame, marked by its icon
   * (red is reserved for absences and the offline strip). ghost: a word
   * underlined in ink, under a main action.
   */
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  disabled?: boolean
  /** Shows a spinner at the same width and blocks a second tap. */
  busy?: boolean
  icon?: IconName
  type?: 'button' | 'submit'
  className?: string
}) {
  return (
    <button
      type={type}
      className={`ui-btn ui-btn--${variant} t-button pressable ${className}`}
      onClick={busy ? undefined : onClick}
      disabled={disabled}
      aria-busy={busy || undefined}
    >
      <span className="ui-btn__content" data-hidden={busy || undefined}>
        {icon ? <AppIcon name={icon} size={20} /> : null}
        {children}
      </span>
      {busy ? <span className="ui-spinner" aria-hidden="true" /> : null}
    </button>
  )
}

export function IconButton({
  icon,
  label,
  onClick,
  disabled,
  tone = 'default',
}: {
  icon: IconName
  label: string
  onClick: () => void
  disabled?: boolean
  /** `muted` for a button repeated on every row of a list. */
  tone?: 'default' | 'muted'
}) {
  return (
    <button
      type="button"
      className={`ui-icon-btn ui-icon-btn--${tone} pressable`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      <AppIcon name={icon} size={24} />
    </button>
  )
}

// ── Fields ───────────────────────────────────────────────────────────────────

interface FieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string | null
  hint?: string
  /** Shown as a small mark beside the label. */
  optionalLabel?: string
  placeholder?: string
  type?: 'text' | 'password' | 'search'
  inputMode?: 'text' | 'tel' | 'search'
  autoComplete?: string
  enterKeyHint?: 'next' | 'done' | 'search' | 'send' | 'go'
  maxLength?: number
  autoFocus?: boolean
  multiline?: boolean
  onEnter?: () => void
  trailing?: ReactNode
  disabled?: boolean
}

function FieldLabel({
  htmlFor,
  label,
  optionalLabel,
}: { htmlFor?: string; label: string; optionalLabel?: string }) {
  const Tag = htmlFor ? 'label' : 'span'
  return (
    <Tag htmlFor={htmlFor} className="ui-field__label t-label-sm">
      {label}
      {optionalLabel ? <span className="ui-field__optional">{optionalLabel}</span> : null}
    </Tag>
  )
}

/** Label above, a 2px underline in mono type, the error below in italics. */
export function TextField(props: FieldProps) {
  const id = useId()
  const errorId = `${id}-error`
  const common = {
    id,
    className: `ui-input ${props.multiline ? 'ui-input--multiline' : ''}`,
    value: props.value,
    placeholder: props.placeholder,
    maxLength: props.maxLength,
    autoFocus: props.autoFocus,
    disabled: props.disabled,
    'aria-invalid': props.error ? true : undefined,
    'aria-describedby': props.error ? errorId : undefined,
    enterKeyHint: props.enterKeyHint,
  } as const
  return (
    <div className="ui-field">
      <FieldLabel htmlFor={id} label={props.label} optionalLabel={props.optionalLabel} />
      <div className="ui-field__box" data-invalid={props.error ? true : undefined}>
        {props.multiline ? (
          <textarea {...common} rows={3} onChange={(e) => props.onChange(e.target.value)} />
        ) : (
          <input
            {...common}
            type={props.type ?? 'text'}
            inputMode={props.inputMode}
            autoComplete={props.autoComplete}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => props.onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && props.onEnter) {
                e.preventDefault()
                props.onEnter()
              }
            }}
          />
        )}
        {props.trailing}
      </div>
      {props.error ? (
        <div id={errorId} className="ui-field__error t-body-sm" role="alert">
          {props.error}
        </div>
      ) : props.hint ? (
        <div className="ui-field__hint t-body-sm">{props.hint}</div>
      ) : null}
    </div>
  )
}

export function PasswordField(props: Omit<FieldProps, 'type' | 'trailing'>) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(false)
  return (
    <TextField
      {...props}
      type={shown ? 'text' : 'password'}
      trailing={
        <button
          type="button"
          className="ui-field__action pressable"
          onClick={() => setShown((v) => !v)}
          aria-label={shown ? t('passwordHide') : t('passwordShow')}
        >
          <AppIcon name={shown ? 'hide' : 'show'} size={20} />
        </button>
      }
    />
  )
}

/**
 * Date picker that looks the same everywhere: our Intl text on top, the native
 * control invisible over it (pwa_starter DateField — iOS draws a Vietnamese
 * date wider than its box otherwise).
 */
export function DateField({
  label,
  value,
  onChange,
  error,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string | null
  hint?: string
}) {
  const { i18n } = useTranslation()
  const text = useMemo(() => {
    if (!value) return ''
    const { year, month, day } = parseDayKey(value)
    return new Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(year, month - 1, day))
  }, [value, i18n.language])
  return (
    <div className="ui-field">
      <FieldLabel label={label} />
      <div className="ui-field__box ui-overlay-field">
        <span className="ui-overlay-field__text">{text}</span>
        <AppIcon name="meetings" size={20} />
        <input
          className="ui-overlay-field__native"
          type="date"
          value={value}
          aria-label={label}
          required
          onChange={(e) => e.target.value && onChange(e.target.value)}
        />
      </div>
      {error ? (
        <div className="ui-field__error t-body-sm" role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="ui-field__hint t-body-sm">{hint}</div>
      ) : null}
    </div>
  )
}

/** Optional time of day, same overlay technique, with a clear button. */
export function TimeField({
  label,
  value,
  onChange,
  optionalLabel,
  emptyText,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  optionalLabel?: string
  emptyText: string
}) {
  const { t } = useTranslation()
  return (
    <div className="ui-field">
      <FieldLabel label={label} optionalLabel={optionalLabel} />
      <div className="ui-field__box ui-overlay-field">
        <span className={`ui-overlay-field__text ${value ? '' : 'ui-overlay-field__text--empty'}`}>
          {value || emptyText}
        </span>
        <AppIcon name="time" size={20} />
        <input
          className="ui-overlay-field__native"
          type="time"
          value={value}
          aria-label={label}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {value ? (
        <button type="button" className="ui-link t-label-sm" onClick={() => onChange('')}>
          {t('timeClear')}
        </button>
      ) : null}
    </div>
  )
}

// ── Choice ───────────────────────────────────────────────────────────────────

/** A square toggle. Selected = inverted. Rows wrap; nothing scrolls sideways. */
export function Chip({
  children,
  selected = false,
  onClick,
}: {
  children: ReactNode
  selected?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="ui-chip t-body-sm pressable"
      data-selected={selected || undefined}
      aria-pressed={selected}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div className="ui-segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          data-selected={o.value === value || undefined}
          className="t-button pressable"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Filter tabs that carry their own counts, as one row of text; the open tab
 * is underlined. Replaces the black figures bar, which shouted on a phone.
 */
export function StatTabs<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string; count: number }[]
  value: T
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div className="ui-stat-tabs" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          data-selected={o.value === value || undefined}
          className="ui-stat-tab"
          onClick={() => onChange(o.value)}
        >
          <span className="t-body-sm">{o.label}</span>
          <span className="t-body-sm ui-stat-tab__count">{o.count}</span>
        </button>
      ))}
    </div>
  )
}

/** Creation on a list whose main job is opening its items. An ink block. */
export function Fab({
  icon,
  label,
  onClick,
}: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button type="button" className="ui-fab t-button pressable" onClick={onClick}>
      <AppIcon name={icon} size={20} />
      {label}
    </button>
  )
}

// ── Status ───────────────────────────────────────────────────────────────────

export const STATUS_KEY: Record<AttendanceStatus, string> = {
  present: 'statusPresent',
  excused: 'statusExcused',
  absent: 'statusAbsent',
  unmarked: 'statusUnmarked',
}

/** An ink strip: something the reader must do. One at a time. */
export function Banner({
  icon,
  children,
  action,
}: {
  icon: IconName
  children: ReactNode
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="ui-banner" role="status">
      <AppIcon name={icon} size={20} />
      <div className="ui-banner__text t-body-sm">{children}</div>
      {action ? (
        <button
          type="button"
          className="ui-banner__action t-label pressable"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  )
}

// ── Sheet (vaul) ─────────────────────────────────────────────────────────────

/**
 * The one bottom-sheet helper: a sheet of paper ruled off at the top. Drag
 * from the handle only, footer pinned above the keyboard (pwa_starter Sheet).
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  children,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false} handleOnly>
      <Drawer.Portal>
        <Drawer.Overlay className="ui-sheet-overlay" />
        <Drawer.Content className="ui-sheet-content" aria-describedby={undefined}>
          <Drawer.Handle className="ui-sheet-handle" />
          <Drawer.Title className="ui-sheet-title t-title">{title}</Drawer.Title>
          <div className={`ui-sheet-body ${footer ? 'ui-sheet-body--with-footer' : ''}`}>
            {children}
          </div>
          {footer ? <div className="ui-sheet-footer">{footer}</div> : null}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

// ── Dialog: only for losing data ─────────────────────────────────────────────

export function Dialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger = false,
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  body?: ReactNode
  confirmLabel: string
  cancelLabel: string
  danger?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  if (!open) return null
  return (
    <div
      className="ui-dialog-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="ui-dialog" role="alertdialog" aria-modal="true" aria-label={title}>
        <div className="t-title">{title}</div>
        {body ? <div className="ui-dialog__body t-body-sm">{body}</div> : null}
        <div className="ui-dialog__actions">
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant="primary" icon={danger ? 'logout' : undefined} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Empty state, skeleton ────────────────────────────────────────────────────

export function EmptyState({
  title,
  body,
  action,
}: {
  /** Kept for call-site compatibility; empty states are typographic, not iconic. */
  icon?: IconName
  title: string
  body?: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="ui-empty">
      <div className="ui-empty__rule" aria-hidden="true" />
      <div className="t-italic">{title}</div>
      {body ? <div className="t-body-sm t-muted">{body}</div> : null}
      {action ? (
        <div className="ui-empty__action">
          <Button onClick={action.onClick}>{action.label}</Button>
        </div>
      ) : null}
    </div>
  )
}

export function RouteSkeleton() {
  return (
    <div className="ui-skeleton" aria-hidden="true">
      <div className="ui-skeleton__bar" style={{ height: 40, width: '60%' }} />
      <div className="ui-skeleton__rule" />
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="ui-skeleton__bar"
          style={{ height: 20, width: `${70 - i * 10}%` }}
        />
      ))}
    </div>
  )
}

// ── Month navigator ──────────────────────────────────────────────────────────

export function MonthNavigator({
  value,
  onChange,
}: {
  value: YearMonth
  onChange: (ym: YearMonth) => void
}) {
  const { t, i18n } = useTranslation()
  const label = useMemo(() => {
    const text = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(
      new Date(value.year, value.month - 1, 1),
    )
    return text.charAt(0).toLocaleUpperCase(i18n.language) + text.slice(1)
  }, [value, i18n.language])
  return (
    <div className="ui-month-nav">
      <IconButton
        icon="back"
        label={t('monthPrevious')}
        onClick={() => onChange(addMonths(value, -1))}
      />
      <div className="t-italic ui-month-nav__label" aria-live="polite" data-month={monthKey(value)}>
        {label}
      </div>
      <IconButton
        icon="forward"
        label={t('monthNext')}
        onClick={() => onChange(addMonths(value, 1))}
      />
    </div>
  )
}

// ── Toast (with optional undo) ───────────────────────────────────────────────

type ToastKind = 'default' | 'error'

interface ToastOptions {
  kind?: ToastKind
  action?: { label: string; onClick: () => void }
}

interface ToastState extends ToastOptions {
  id: number
  message: string
}

const ToastContext = createContext<(message: string, options?: ToastOptions) => void>(() => {})

/** An ink block. ~2s plain, ~4s with an action. Never for input errors. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const counter = useRef(0)

  const show = useCallback((message: string, options: ToastOptions = {}) => {
    if (timer.current) clearTimeout(timer.current)
    counter.current += 1
    setToast({ id: counter.current, message, ...options })
    timer.current = setTimeout(() => setToast(null), options.action ? 4000 : 2200)
  }, [])

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), [])

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toast ? (
        <div className="ui-toast-wrap">
          <div key={toast.id} className="ui-toast" data-kind={toast.kind} role="status">
            <span className="t-body-sm">{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                className="ui-toast__action t-label"
                onClick={() => {
                  toast.action?.onClick()
                  setToast(null)
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  )
}

export function useToast() {
  return useContext(ToastContext)
}

// ── Page scaffolding ─────────────────────────────────────────────────────────

/**
 * Two headers. A tab screen: an optional small overline (e.g. today's date),
 * the Playfair title, one thin rule. A stacked screen: back, overline, title.
 */
export function PageHeader({
  title,
  subtitle,
  overline,
  onBack,
  backLabel,
  trailing,
  search,
}: {
  title: string
  /** Under the title on stacked screens. */
  subtitle?: string
  /** Above the title: a date, a time. */
  overline?: string
  onBack?: () => void
  backLabel?: string
  trailing?: ReactNode
  /**
   * Search mode: the bar becomes the search field (stacked screens only).
   * Back closes the search; the field clears with its own button.
   */
  search?: {
    value: string
    onChange: (value: string) => void
    onClose: () => void
    placeholder: string
    label: string
    closeLabel: string
    clearLabel: string
  }
}) {
  if (search) {
    return (
      <header className="page-header">
        <IconButton icon="back" label={search.closeLabel} onClick={search.onClose} />
        <div className="ui-field__box page-header__search">
          <input
            className="ui-input"
            // text, not search: the bar has its own clear button, browsers add another
            type="text"
            inputMode="search"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && search.onClose()}
            placeholder={search.placeholder}
            aria-label={search.label}
            enterKeyHint="search"
            autoFocus
          />
        </div>
        {search.value ? (
          <IconButton icon="close" label={search.clearLabel} onClick={() => search.onChange('')} />
        ) : null}
      </header>
    )
  }
  if (!onBack) {
    return (
      <header className="masthead">
        {overline ? <div className="t-label-sm masthead__overline">{overline}</div> : null}
        <div className="masthead__row">
          <h1 className="t-display masthead__title">{title}</h1>
          {trailing ? <div className="masthead__trailing">{trailing}</div> : null}
        </div>
      </header>
    )
  }
  return (
    <header className="page-header">
      <IconButton icon="back" label={backLabel ?? ''} onClick={onBack} />
      <div className="page-header__titles">
        {overline ? (
          <div className="t-label-sm t-muted page-header__subtitle">{overline}</div>
        ) : null}
        <h1 className="t-title page-header__title">{title}</h1>
        {subtitle ? (
          <div className="t-body-sm t-muted page-header__subtitle">{subtitle}</div>
        ) : null}
      </div>
      {trailing ? <div className="page-header__trailing">{trailing}</div> : null}
    </header>
  )
}

/** Two (or more) big answers; tapping one is the decision, no Save needed. */
export function ChoiceRow<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string; tone?: 'accent' }[]
  value: T | null
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div className="ui-choice-row" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          data-selected={o.value === value || undefined}
          data-tone={o.tone}
          className="ui-choice"
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
